import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import {
  Activity,
  AtSign,
  Ban,
  CheckCircle2,
  ChevronRight,
  CircleGauge,
  Clock3,
  Cloud,
  Copy,
  Globe2,
  Inbox,
  KeyRound,
  Languages,
  LogOut,
  Mail,
  Menu,
  Moon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Route,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Sun,
  Trash2,
  Users,
  UserPlus,
  X,
} from "lucide-react";
import QRCode from "qrcode";
import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { MessageView, type MessagePayload } from "./message-view";
import "./styles.css";

type User = { id: string; email: string; role: string; totp_enabled: number };
type AuthStatus = { initialized: boolean; authenticated: boolean; user: User | null };
type Totals = { messages: number; unread: number; addresses: number; temporary: number; bytes: number; forwardingRate: number };
type MessageItem = {
  id: string;
  direction: string;
  status: string;
  from_address: string;
  to_addresses: string;
  subject: string;
  preview: string;
  verification_code: string | null;
  is_read: number;
  is_starred: number;
  size: number;
  expires_at: string | null;
  created_at: string;
};
type AddressItem = {
  id: string;
  address: string;
  name: string | null;
  kind: string;
  is_active: number;
  expires_at: string | null;
  retention_days: number;
  forward_to: string;
  native_route_id: string | null;
  created_at: string;
};
type RuleItem = {
  id: string;
  name: string;
  priority: number;
  match_field: string;
  match_type: string;
  pattern: string;
  destinations: string;
  enabled: number;
};
type PolicyItem = { id: string; list_type: string; match_type: string; value: string; note: string | null };
type DashboardData = { totals: Totals; recent: MessageItem[]; domain: string };
type GmailStatus = { configured: boolean; connection: { gmail_address?: string; status?: string } | null };
type RoutingStatus = { connected: boolean; native: unknown[]; local: RuleItem[] };
type DomainItem = { id: string; name: string; zone_id: string | null; status: string; is_primary: number; created_at: string };
type MemberItem = { id: string; email: string; role: "admin" | "operator" | "viewer"; status: "active" | "disabled"; totp_enabled: number; created_at: string; updated_at: string };
type PageKey = "overview" | "messages" | "addresses" | "routing" | "people" | "security" | "integrations" | "settings";
type Theme = "light" | "dark";

const nav: Array<{ key: PageKey; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { key: "overview", label: "概览", icon: CircleGauge },
  { key: "messages", label: "邮件", icon: Inbox },
  { key: "addresses", label: "地址管理", icon: AtSign },
  { key: "routing", label: "转发规则", icon: Route },
  { key: "people", label: "人员管理", icon: Users },
  { key: "security", label: "身份安全", icon: ShieldCheck },
  { key: "integrations", label: "连接", icon: Cloud },
  { key: "settings", label: "设置", icon: Settings },
];

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  const payload = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || "请求失败");
  return payload;
}

function parseList(value: string): string[] {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function formatDate(value: string | null): string {
  if (!value) return "长期";
  return new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function copy(value: string) {
  void navigator.clipboard.writeText(value);
}

function Login({ onAuthenticated, theme, onTheme }: { onAuthenticated: () => void; theme: Theme; onTheme: () => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState(() => new URLSearchParams(window.location.search).get("login") === "invalid" ? "登录链接已失效，请重新发送。" : "");
  const [busy, setBusy] = useState(false);

  async function magic() {
    setBusy(true);
    try {
      await api("/api/auth/magic", { method: "POST", body: JSON.stringify({ email }) });
      setMessage("登录链接已发送，请检查邮箱。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "发送失败");
    } finally {
      setBusy(false);
    }
  }

  async function totp() {
    setBusy(true);
    try {
      await api("/api/auth/totp/login", { method: "POST", body: JSON.stringify({ email, code }) });
      onAuthenticated();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }

  async function passkey() {
    setBusy(true);
    try {
      const result = await api<{ flow: string; options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>("/api/auth/passkey/options", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      const response = await startAuthentication({ optionsJSON: result.options });
      await api("/api/auth/passkey/verify", { method: "POST", body: JSON.stringify({ flow: result.flow, response }) });
      onAuthenticated();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Passkey 登录失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <button className="icon-button login-theme" title="切换主题" onClick={onTheme}>{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
      <section className="login-panel">
        <div className="brand brand-large"><span className="brand-mark"><Mail size={22} /></span><span>NexMail</span></div>
        <div className="login-copy">
          <p className="eyebrow">企业邮件控制台</p>
          <h1>登录到邮件平台</h1>
          <p>管理地址、邮件、转发规则与安全设置。</p>
        </div>
        <label className="field"><span>管理员邮箱</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" /></label>
        <label className="field"><span>动态验证码</span><input inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} placeholder="可选" /></label>
        <button className="button primary wide" disabled={!email || busy} onClick={passkey}><KeyRound size={17} />使用 Passkey</button>
        <button className="button secondary wide" disabled={!email || code.length !== 6 || busy} onClick={totp}>使用动态验证码</button>
        <button className="text-button" disabled={!email || busy} onClick={magic}>发送登录链接</button>
        {message && <div className="notice">{message}</div>}
        <div className="login-footer"><Cloud size={16} />运行于 Cloudflare 全球网络</div>
      </section>
      <section className="login-art">
        <div className="mail-orbit orbit-one"><AtSign /></div>
        <div className="mail-orbit orbit-two"><Route /></div>
        <div className="mail-orbit orbit-three"><ShieldCheck /></div>
        <div className="hero-card">
          <Sparkles size={22} />
          <strong>邮件流统一管理</strong>
          <span>接收、解析、存储、转发与发送</span>
        </div>
      </section>
    </main>
  );
}

function StatCard({ label, value, detail, icon: Icon }: { label: string; value: string; detail: string; icon: React.ComponentType<{ size?: number }> }) {
  return <div className="stat-card"><div className="stat-icon"><Icon size={19} /></div><div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div></div>;
}

function Empty({ title, text }: { title: string; text: string }) {
  return <div className="empty"><div className="empty-icon"><Mail /></div><strong>{title}</strong><span>{text}</span></div>;
}

function messageIdFromPath(): string | null {
  const match = window.location.pathname.match(/^\/messages\/([^/]+)$/u);
  return match ? decodeURIComponent(match[1]!) : null;
}

function App() {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [page, setPage] = useState<PageKey>(() => messageIdFromPath() ? "messages" : "overview");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [rules, setRules] = useState<RuleItem[]>([]);
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [gmail, setGmail] = useState<GmailStatus | null>(null);
  const [routing, setRouting] = useState<RoutingStatus | null>(null);
  const [domains, setDomains] = useState<DomainItem[]>([]);
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [selected, setSelected] = useState<MessagePayload | null>(null);
  const [messageId, setMessageId] = useState<string | null>(() => messageIdFromPath());
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState("");
  const [compose, setCompose] = useState(false);
  const [addressForm, setAddressForm] = useState(false);
  const [ruleForm, setRuleForm] = useState(false);
  const [policyForm, setPolicyForm] = useState(false);
  const [memberForm, setMemberForm] = useState(false);
  const [totp, setTotp] = useState<{ secret: string; uri: string; qr: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem("nexmail-theme");
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  async function loadStatus() {
    const result = await api<AuthStatus>("/api/auth/status");
    setStatus(result);
    return result;
  }

  async function loadData(target: PageKey = page) {
    setLoading(true);
    try {
      const requests: Array<Promise<unknown>> = [api<DashboardData>("/api/dashboard").then(setDashboard)];
      if (target === "overview" || target === "messages") {
        requests.push(api<{ items: DomainItem[] }>("/api/domains").then((result) => setDomains(result.items)));
        requests.push(api<GmailStatus>("/api/integrations/gmail/status").then(setGmail));
      }
      if (target === "messages") requests.push(api<{ items: MessageItem[] }>("/api/messages").then((result) => setMessages(result.items)));
      if (target === "addresses") {
        requests.push(api<{ items: AddressItem[] }>("/api/addresses").then((result) => setAddresses(result.items)));
        requests.push(api<{ items: DomainItem[] }>("/api/domains").then((result) => setDomains(result.items)));
        requests.push(api<RoutingStatus>("/api/routing").then(setRouting));
      }
      if (target === "routing") {
        requests.push(api<{ items: RuleItem[] }>("/api/rules").then((result) => setRules(result.items)));
        requests.push(api<{ items: PolicyItem[] }>("/api/policies").then((result) => setPolicies(result.items)));
        requests.push(api<RoutingStatus>("/api/routing").then(setRouting));
      }
      if (target === "people") requests.push(api<{ items: MemberItem[] }>("/api/users").then((result) => setMembers(result.items)));
      if (target === "integrations") requests.push(api<GmailStatus>("/api/integrations/gmail/status").then(setGmail));
      if (target === "settings") requests.push(api<{ items: DomainItem[] }>("/api/domains").then((result) => setDomains(result.items)));
      await Promise.all(requests);
    } catch (error) {
      if (error instanceof Error && error.message === "请先登录") setStatus((current) => current ? { ...current, authenticated: false, user: null } : current);
      else setToast(error instanceof Error ? error.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const id = messageIdFromPath();
    void loadStatus().then(async (result) => {
      if (!result.authenticated) return setLoading(false);
      await loadData(id ? "messages" : "overview");
      if (id) await openMessage(id, false);
    });
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("nexmail-theme", theme);
  }, [theme]);

  useEffect(() => {
    const syncPath = () => {
      const id = messageIdFromPath();
      setMessageId(id);
      setSelected(null);
      if (id) {
        setPage("messages");
        void openMessage(id, false);
      }
    };
    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = window.setTimeout(() => setToast(""), 3000);
      return () => window.clearTimeout(timer);
    }
  }, [toast]);

  const filtered = useMemo(() => messages.filter((item) => `${item.subject} ${item.from_address} ${item.preview}`.toLowerCase().includes(search.toLowerCase())), [messages, search]);

  async function openMessage(id: string, updateUrl = true) {
    setMessageId(id);
    setSelected(null);
    setLoading(true);
    if (updateUrl) window.history.pushState(null, "", `/messages/${encodeURIComponent(id)}`);
    try {
      const result = await api<MessagePayload>(`/api/messages/${id}`);
      setSelected(result);
      setMessages((items) => items.map((item) => item.id === id ? { ...item, is_read: 1 } : item));
    } catch (error) {
      setToast(error instanceof Error ? error.message : "邮件加载失败");
      setMessageId(null);
      window.history.replaceState(null, "", "/");
    } finally {
      setLoading(false);
    }
  }

  function navigatePage(target: PageKey) {
    setPage(target);
    setSelected(null);
    setMessageId(null);
    setMobileMenu(false);
    window.history.pushState(null, "", "/");
    void loadData(target);
  }

  function closeMessage() {
    setSelected(null);
    setMessageId(null);
    setPage("messages");
    window.history.pushState(null, "", "/");
    void loadData("messages");
  }

  async function remove(path: string, question: string): Promise<boolean> {
    if (!window.confirm(question)) return false;
    await api(path, { method: "DELETE" });
    setToast("已删除");
    await loadData();
    return true;
  }

  async function updateMember(id: string, change: { role?: string; status?: string }) {
    try {
      await api(`/api/users/${id}`, { method: "PATCH", body: JSON.stringify(change) });
      setToast("成员已更新");
      await Promise.all([loadData("people"), loadStatus()]);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "更新失败");
    }
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setStatus((current) => current ? { ...current, authenticated: false, user: null } : current);
  }

  async function addPasskey() {
    try {
      const options = await api<Parameters<typeof startRegistration>[0]["optionsJSON"]>("/api/auth/passkey/register/options", { method: "POST" });
      const response = await startRegistration({ optionsJSON: options });
      await api("/api/auth/passkey/register/verify", { method: "POST", body: JSON.stringify(response) });
      setToast("Passkey 已添加");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Passkey 添加失败");
    }
  }

  async function startTotp() {
    const result = await api<{ secret: string; uri: string }>("/api/auth/totp/start", { method: "POST" });
    setTotp({ ...result, qr: await QRCode.toDataURL(result.uri, { margin: 1, width: 220 }) });
  }

  async function syncGmail() {
    try {
      const result = await api<{ synced: number }>("/api/integrations/gmail/sync", { method: "POST" });
      setToast(`已读取 ${result.synced} 封 Gmail 邮件`);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Gmail 同步失败");
    }
  }

  async function syncAddressRoutes() {
    try {
      const result = await api<{ synced: number }>("/api/addresses/routes/sync", { method: "POST" });
      setToast(`已同步 ${result.synced} 个收件地址`);
      await loadData();
    } catch (error) {
      setToast(error instanceof Error ? error.message : "地址同步失败");
    }
  }

  if (!status || loading && !status.authenticated) return <div className="boot"><div className="brand"><span className="brand-mark"><Mail size={18} /></span><span>NexMail</span></div><div className="spinner" /></div>;
  if (!status.authenticated) return <Login theme={theme} onTheme={() => setTheme((value) => value === "dark" ? "light" : "dark")} onAuthenticated={() => void loadStatus().then(async () => {
    await loadData(messageId ? "messages" : "overview");
    if (messageId) await openMessage(messageId, false);
  })} />;

  const visibleNav = nav.filter((item) => item.key !== "people" || status.user?.role === "admin");
  const title = selected?.message.subject || nav.find((item) => item.key === page)?.label || "概览";
  return (
    <div className="shell">
      <aside className={`sidebar ${mobileMenu ? "open" : ""}`}>
        <div className="brand"><span className="brand-mark"><Mail size={18} /></span><span>NexMail</span></div>
        <div className="account-card"><div className="avatar">N</div><div><strong>{dashboard?.domain || "nex2.net"}</strong><span>{domains.length || 1} 个域名</span></div><MoreHorizontal size={17} /></div>
        <nav>{visibleNav.map(({ key, label, icon: Icon }) => <button key={key} className={page === key && !messageId ? "active" : ""} onClick={() => navigatePage(key)}><Icon size={18} /><span>{label}</span></button>)}</nav>
        <div className="sidebar-footer"><div><span className="status-dot" />服务正常</div><button onClick={logout}><LogOut size={17} />退出</button></div>
      </aside>
      {mobileMenu && <button className="backdrop" aria-label="关闭菜单" onClick={() => setMobileMenu(false)} />}
      <main className="main">
        <header className="topbar">
          <button className="icon-button menu-button" onClick={() => setMobileMenu(true)}><Menu size={20} /></button>
          <div className="breadcrumb"><span>邮件平台</span><ChevronRight size={14} /><strong>{title}</strong></div>
          <div className="top-actions"><button className="icon-button" title="切换主题" onClick={() => setTheme((value) => value === "dark" ? "light" : "dark")}>{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button><button className="icon-button" title="刷新" onClick={() => void (messageId ? openMessage(messageId, false) : loadData())}><RefreshCw size={18} /></button><div className="user-pill"><div className="avatar small">{status.user?.email[0]?.toUpperCase()}</div><span>{status.user?.email}</span></div></div>
        </header>
        <div className={`content ${messageId ? "message-content" : ""}`}>
          {messageId ? selected ? <MessageView data={selected} onBack={closeMessage} onDelete={() => void remove(`/api/messages/${selected.message.id}`, "确认永久删除此邮件及附件？").then((deleted) => { if (deleted) closeMessage(); })} onTranslated={(text) => setSelected((current) => current ? { ...current, message: { ...current.message, text_body: text } } : current)} /> : <PageSkeleton /> : <>
          {page === "overview" && <>
            <div className="page-heading"><div><p className="eyebrow">运行概况</p><h1>邮件平台概览</h1><p>查看收件、地址和转发状态。</p></div><button className="button primary" onClick={() => setCompose(true)}><Send size={17} />写邮件</button></div>
            <div className="stats-grid">
              <StatCard label="邮件总数" value={String(dashboard?.totals.messages || 0)} detail={`${dashboard?.totals.unread || 0} 封未读`} icon={Mail} />
              <StatCard label="有效地址" value={String(dashboard?.totals.addresses || 0)} detail={`${dashboard?.totals.temporary || 0} 个临时地址`} icon={AtSign} />
              <StatCard label="转发成功率" value={`${dashboard?.totals.forwardingRate ?? 100}%`} detail="全部转发记录" icon={Activity} />
              <StatCard label="邮件容量" value={formatBytes(dashboard?.totals.bytes || 0)} detail="正文与原始邮件" icon={Cloud} />
            </div>
            <section className="panel">
              <div className="panel-head"><div><h2>最近邮件</h2><p>最新收到和发出的邮件</p></div><button className="text-button" onClick={() => navigatePage("messages")}>查看全部</button></div>
              <MessageTable items={dashboard?.recent || []} onOpen={openMessage} />
            </section>
            <div className="two-column">
              <section className="panel compact"><div className="panel-head"><div><h2>收件流程</h2><p>邮件处理状态</p></div><CheckCircle2 className="success" size={22} /></div><div className="flow"><span>接收</span><i /><span>解析</span><i /><span>存储</span><i /><span>转发</span></div></section>
              <section className="panel compact"><div className="panel-head"><div><h2>安全状态</h2><p>管理员身份保护</p></div><ShieldCheck className="success" size={22} /></div><div className="security-line"><span>管理员</span><strong>{status.user?.email}</strong></div><div className="security-line"><span>动态验证码</span><strong>{status.user?.totp_enabled ? "已启用" : "待启用"}</strong></div></section>
            </div>
          </>}

          {page === "messages" && <>
            <div className="page-heading"><div><p className="eyebrow">邮件管理</p><h1>全部邮件</h1><p>查询、查看与管理邮件。</p></div><button className="button primary" onClick={() => setCompose(true)}><Send size={17} />写邮件</button></div>
            <section className="panel"><div className="toolbar"><div className="search"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索发件人、主题或正文" /></div><span className="count">{filtered.length} 封邮件</span></div><MessageTable items={filtered} onOpen={openMessage} /></section>
          </>}

          {page === "addresses" && <>
            <div className="page-heading"><div><p className="eyebrow">地址管理</p><h1>邮箱与别名</h1><p>创建正式地址、别名和临时地址。</p></div><div className="heading-actions"><button className="button secondary" disabled={!routing?.connected} onClick={() => void syncAddressRoutes()}><RefreshCw size={17} />同步收件</button><button className="button primary" onClick={() => setAddressForm(true)}><Plus size={17} />创建地址</button></div></div>
            <section className="panel"><div className="panel-head"><div><h2>地址列表</h2><p>{addresses.filter((item) => item.is_active).length} 个有效地址</p></div></div>{addresses.length ? <div className="table-wrap"><table><thead><tr><th>地址</th><th>类型</th><th>收件</th><th>保留时间</th><th>到期时间</th><th>转发目标</th><th /></tr></thead><tbody>{addresses.map((item) => <tr key={item.id} className={!item.is_active ? "muted-row" : ""}><td><div className="address-cell"><span className="address-icon"><AtSign size={16} /></span><div><strong>{item.address}</strong><span>{item.name || "未命名"}</span></div><button className="copy-button" onClick={() => copy(item.address)}><Copy size={14} /></button></div></td><td><Badge value={item.kind} /></td><td>{item.native_route_id ? "已接入" : "全收件"}</td><td>{item.retention_days} 天</td><td>{formatDate(item.expires_at)}</td><td>{parseList(item.forward_to).join(", ") || "保存在平台"}</td><td><button className="icon-button danger" title="停用" onClick={() => void remove(`/api/addresses/${item.id}`, "确认停用此地址？")}><Trash2 size={16} /></button></td></tr>)}</tbody></table></div> : <Empty title="还没有地址" text="创建第一个邮箱或临时地址。" />}</section>
          </>}

          {page === "routing" && <>
            <div className="page-heading"><div><p className="eyebrow">邮件流</p><h1>转发规则</h1><p>按地址、发件人、主题或正文转发。</p></div><button className="button primary" onClick={() => setRuleForm(true)}><Plus size={17} />添加规则</button></div>
            <div className="callout"><Route size={20} /><div><strong>原生收件路由</strong><span>{routing?.connected ? `已读取 ${routing.native.length} 条 Cloudflare 路由。` : "收件已启用，读取列表需要配置访问凭据。"}</span></div></div>
            <section className="panel"><div className="panel-head"><div><h2>平台规则</h2><p>按优先级依次执行</p></div></div>{rules.length ? <div className="rule-list">{rules.map((item) => <div className="rule-row" key={item.id}><div className="priority">{item.priority}</div><div className="rule-main"><strong>{item.name}</strong><span>{item.match_field} · {item.match_type} · {item.pattern || "全部"}</span></div><div className="chips">{parseList(item.destinations).map((destination) => <span key={destination}>{destination}</span>)}</div><Badge value={item.enabled ? "enabled" : "disabled"} /><button className="icon-button danger" onClick={() => void remove(`/api/rules/${item.id}`, "确认删除此规则？")}><Trash2 size={16} /></button></div>)}</div> : <Empty title="还没有转发规则" text="添加规则后可自动转发邮件。" />}</section>
            <section className="panel"><div className="panel-head"><div><h2>黑白名单</h2><p>白名单优先于黑名单</p></div><button className="button secondary" onClick={() => setPolicyForm(true)}><Plus size={16} />添加名单</button></div>{policies.length ? <div className="policy-grid">{policies.map((item) => <div className="policy-card" key={item.id}><span className={item.list_type === "allow" ? "policy allow" : "policy block"}>{item.list_type === "allow" ? <ShieldCheck size={16} /> : <Ban size={16} />}{item.list_type === "allow" ? "允许" : "拦截"}</span><strong>{item.value}</strong><small>{item.match_type}</small><button className="icon-button danger" onClick={() => void remove(`/api/policies/${item.id}`, "确认删除此名单项？")}><Trash2 size={15} /></button></div>)}</div> : <Empty title="名单为空" text="当前不限制发件地址。" />}</section>
          </>}

          {page === "people" && <>
            <div className="page-heading"><div><p className="eyebrow">人员管理</p><h1>平台成员</h1><p>设置管理员、操作员和只读成员。</p></div><button className="button primary" onClick={() => setMemberForm(true)}><UserPlus size={17} />添加成员</button></div>
            <section className="panel"><div className="panel-head"><div><h2>成员列表</h2><p>{members.filter((item) => item.status === "active").length} 名有效成员</p></div></div>{members.length ? <div className="table-wrap"><table><thead><tr><th>成员</th><th>角色</th><th>登录保护</th><th>加入时间</th><th>状态</th></tr></thead><tbody>{members.map((member) => <tr key={member.id} className={member.status === "disabled" ? "muted-row" : ""}><td><div className="member-cell"><div className="avatar small">{member.email[0]?.toUpperCase()}</div><div><strong>{member.email}</strong>{member.id === status.user?.id && <span>当前账户</span>}</div></div></td><td><select className="table-select" value={member.role} onChange={(event) => void updateMember(member.id, { role: event.target.value })}><option value="admin">管理员</option><option value="operator">操作员</option><option value="viewer">只读</option></select></td><td>{member.totp_enabled ? "动态验证码" : "登录链接"}</td><td>{formatDate(member.created_at)}</td><td><button className={`status-button ${member.status}`} disabled={member.id === status.user?.id} onClick={() => void updateMember(member.id, { status: member.status === "active" ? "disabled" : "active" })}>{member.status === "active" ? "已启用" : "已停用"}</button></td></tr>)}</tbody></table></div> : <Empty title="还没有成员" text="添加成员后可分配平台角色。" />}</section>
          </>}

          {page === "security" && <>
            <div className="page-heading"><div><p className="eyebrow">身份安全</p><h1>登录与验证</h1><p>配置 Passkey 和动态验证码。</p></div></div>
            <div className="settings-grid">
              <section className="setting-card"><div className="setting-icon"><KeyRound /></div><div><h2>Passkey</h2><p>使用设备验证快速登录。</p></div><button className="button secondary" onClick={() => void addPasskey()}>添加 Passkey</button></section>
              <section className="setting-card"><div className="setting-icon"><ShieldCheck /></div><div><h2>动态验证码</h2><p>{status.user?.totp_enabled ? "已启用动态验证码。" : "使用验证器应用生成登录码。"}</p></div><button className="button secondary" onClick={() => void startTotp()}>{status.user?.totp_enabled ? "重新配置" : "开始配置"}</button></section>
              <section className="setting-card"><div className="setting-icon"><Users /></div><div><h2>当前账户</h2><p>{status.user?.email}</p></div><Badge value={status.user?.role || "admin"} /></section>
            </div>
          </>}

          {page === "integrations" && <>
            <div className="page-heading"><div><p className="eyebrow">连接</p><h1>外部服务</h1><p>连接 Gmail 与标准邮件客户端。</p></div></div>
            <div className="integration-grid">
              <section className="integration-card"><div className="integration-logo gmail">M</div><div><h2>Gmail</h2><p>连接 Gmail 并读取邮件。</p>{gmail?.connection?.gmail_address && <span className="connected"><CheckCircle2 size={15} />{gmail.connection.gmail_address}</span>}</div>{gmail?.connection && <button className="button secondary" onClick={() => void syncGmail()}>同步</button>}<button className="button secondary" disabled={!gmail?.configured} onClick={() => { window.location.href = "/api/integrations/gmail/connect"; }}>{gmail?.connection ? "重新连接" : "连接"}</button>{!gmail?.configured && <small>需要先配置 Gmail 授权。</small>}</section>
              <section className="integration-card"><div className="integration-logo smtp"><Send /></div><div><h2>SMTP 发件</h2><p>使用 Cloudflare SMTP 安全发送。</p><code>smtp.mx.cloudflare.net:465</code></div><span className="connected"><CheckCircle2 size={15} />可用</span></section>
              <section className="integration-card disabled-card"><div className="integration-logo imap"><Inbox /></div><div><h2>IMAP</h2><p>标准 IMAP 接入等待平台支持。</p></div><Badge value="planned" /></section>
            </div>
          </>}

          {page === "settings" && <>
            <div className="page-heading"><div><p className="eyebrow">平台设置</p><h1>邮件配置</h1><p>查看域名、存储与运行方式。</p></div></div>
            <section className="panel"><div className="panel-head"><div><h2>接入域名</h2><p>用于收件、地址和发件</p></div></div><div className="domain-grid">{domains.map((domain) => <div className="domain-card" key={domain.id}><div className="domain-icon"><Globe2 size={19} /></div><div><strong>{domain.name}</strong><span>{domain.is_primary ? "主域名" : "附加域名"}</span></div><Badge value={domain.status} /></div>)}</div></section>
            <section className="panel settings-list"><SettingLine label="控制台地址" value="mail.nex2.net" detail="管理入口" /><SettingLine label="默认保留时间" value="30 天" detail="到期后自动删除" /><SettingLine label="邮件存储" value="已启用" detail="原始邮件与附件分开保存" /><SettingLine label="智能翻译" value="已启用" detail="按需翻译邮件正文" /><SettingLine label="验证码提取" value="已启用" detail="自动识别常见验证码" /></section>
          </>}
          </>}
        </div>
      </main>
      {compose && <Compose domains={domains.length ? domains : [{ id: "primary", name: dashboard?.domain || "nex2.net", zone_id: null, status: "active", is_primary: 1, created_at: "" }]} gmailAddress={gmail?.connection?.gmail_address} onClose={() => setCompose(false)} onSent={() => { setCompose(false); setToast("邮件已发送"); void loadData(); }} />}
      {addressForm && <AddressForm domains={domains.length ? domains : [{ id: "primary", name: dashboard?.domain || "nex2.net", zone_id: null, status: "active", is_primary: 1, created_at: "" }]} onClose={() => setAddressForm(false)} onCreated={() => { setAddressForm(false); setToast("地址已创建"); void loadData(); }} />}
      {ruleForm && <RuleForm onClose={() => setRuleForm(false)} onCreated={() => { setRuleForm(false); setToast("规则已添加"); void loadData(); }} />}
      {policyForm && <PolicyForm onClose={() => setPolicyForm(false)} onCreated={() => { setPolicyForm(false); setToast("名单已添加"); void loadData(); }} />}
      {memberForm && <MemberForm onClose={() => setMemberForm(false)} onCreated={() => { setMemberForm(false); setToast("成员已添加，登录链接已发送"); void loadData("people"); }} />}
      {totp && <TotpModal value={totp} onClose={() => setTotp(null)} onDone={() => { setTotp(null); setToast("动态验证码已启用"); void loadStatus(); }} />}
      {toast && <div className="toast"><CheckCircle2 size={17} />{toast}</div>}
      {loading && <div className="loading-line" />}
    </div>
  );
}

function MessageTable({ items, onOpen }: { items: MessageItem[]; onOpen: (id: string) => void | Promise<void> }) {
  if (!items.length) return <Empty title="还没有邮件" text="收到或发出邮件后会显示在这里。" />;
  return <div className="message-list">{items.map((item) => <button className={`message-row ${item.is_read ? "" : "unread"}`} key={item.id} onClick={() => void onOpen(item.id)}><div className="message-check"><span /></div><Star size={16} className={item.is_starred ? "starred" : ""} /><div className="sender">{item.direction === "outbound" ? `发给 ${parseList(item.to_addresses)[0] || "收件人"}` : item.from_address}</div><div className="message-copy"><strong>{item.subject || "无主题"}</strong><span>— {item.preview}</span>{item.verification_code && <em>验证码 {item.verification_code}</em>}</div><time>{formatDate(item.created_at)}</time></button>)}</div>;
}

function PageSkeleton() {
  return <div className="page-skeleton"><span /><span /><div><i /><i /><i /></div><section><b /><b /><b /><b /></section></div>;
}

function Badge({ value }: { value: string }) {
  const labels: Record<string, string> = { mailbox: "邮箱", alias: "别名", temporary: "临时", catch_all: "全收", active: "已启用", enabled: "启用", disabled: "停用", admin: "管理员", operator: "操作员", viewer: "只读", planned: "计划中" };
  return <span className={`badge ${value}`}>{labels[value] || value}</span>;
}

function SettingLine({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <div className="setting-line"><div><strong>{label}</strong><span>{detail}</span></div><b>{value}</b></div>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return <div className="modal-layer"><button className="modal-backdrop" onClick={onClose} aria-label="关闭" /><section className="modal"><div className="modal-head"><h2>{title}</h2><button className="icon-button" onClick={onClose}><X size={19} /></button></div>{children}</section></div>;
}

function Compose({ domains, gmailAddress, onClose, onSent }: { domains: DomainItem[]; gmailAddress?: string; onClose: () => void; onSent: () => void }) {
  const primaryDomain = domains.find((item) => item.is_primary)?.name || domains[0]?.name || "nex2.net";
  const [domain, setDomain] = useState(primaryDomain);
  const [form, setForm] = useState({ from: `noreply@${primaryDomain}`, to: "", subject: "", text: "" });
  const [provider, setProvider] = useState("cloudflare");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const path = provider === "gmail" ? "/api/integrations/gmail/send" : "/api/messages/send";
      await api(path, { method: "POST", body: JSON.stringify({ ...form, from: provider === "gmail" ? gmailAddress : form.from, to: form.to.split(",").map((item) => item.trim()).filter(Boolean), cc: [], bcc: [] }) });
      onSent();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "发送失败"); }
    finally { setBusy(false); }
  }
  return <Modal title="写邮件" onClose={onClose}><form className="form" onSubmit={submit}>{gmailAddress && <label className="field"><span>发送方式</span><select value={provider} onChange={(event) => setProvider(event.target.value)}><option value="cloudflare">Cloudflare</option><option value="gmail">Gmail</option></select></label>}{provider === "cloudflare" && domains.length > 1 && <label className="field"><span>发件域名</span><select value={domain} onChange={(event) => { const next = event.target.value; setDomain(next); setForm({ ...form, from: `noreply@${next}` }); }}>{domains.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}</select></label>}<label className="field"><span>发件地址</span><input type="email" value={provider === "gmail" ? gmailAddress : form.from} disabled={provider === "gmail"} onChange={(event) => setForm({ ...form, from: event.target.value })} /></label><label className="field"><span>收件地址</span><input type="text" value={form.to} onChange={(event) => setForm({ ...form, to: event.target.value })} placeholder="多个地址用逗号分隔" /></label><label className="field"><span>主题</span><input value={form.subject} onChange={(event) => setForm({ ...form, subject: event.target.value })} /></label><label className="field"><span>正文</span><textarea rows={9} value={form.text} onChange={(event) => setForm({ ...form, text: event.target.value })} /></label>{error && <div className="error">{error}</div>}<div className="form-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={busy || !form.to || !form.text}><Send size={16} />{busy ? "发送中" : "发送"}</button></div></form></Modal>;
}

function AddressForm({ domains, onClose, onCreated }: { domains: DomainItem[]; onClose: () => void; onCreated: () => void }) {
  const [domain, setDomain] = useState(domains.find((item) => item.is_primary)?.name || domains[0]?.name || "nex2.net");
  const [batch, setBatch] = useState(false);
  const [localPart, setLocalPart] = useState("");
  const [count, setCount] = useState(10);
  const [hours, setHours] = useState(24);
  const [kind, setKind] = useState("mailbox");
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError("");
    try {
      if (batch) await api("/api/addresses/batch", { method: "POST", body: JSON.stringify({ prefix: localPart || "temp", count, domain, expiresInHours: hours, retentionDays: 7, forwardTo: [] }) });
      else await api("/api/addresses", { method: "POST", body: JSON.stringify({ localPart, domain, kind, retentionDays: kind === "temporary" ? 7 : 30, expiresInHours: kind === "temporary" ? hours : undefined, forwardTo: [] }) });
      onCreated();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "创建失败"); }
  }
  return <Modal title="创建地址" onClose={onClose}><form className="form" onSubmit={submit}><div className="segmented"><button type="button" className={!batch ? "active" : ""} onClick={() => setBatch(false)}>单个地址</button><button type="button" className={batch ? "active" : ""} onClick={() => setBatch(true)}>批量临时地址</button></div><label className="field"><span>地址域名</span><select value={domain} onChange={(event) => setDomain(event.target.value)}>{domains.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}</select></label><label className="field"><span>{batch ? "地址前缀" : "地址名称"}</span><div className="joined-input"><input value={localPart} onChange={(event) => setLocalPart(event.target.value.toLowerCase())} placeholder={batch ? "temp" : "support"} /><b>@{domain}</b></div></label>{batch ? <><label className="field"><span>生成数量</span><input type="number" min={1} max={100} value={count} onChange={(event) => setCount(Number(event.target.value))} /></label><label className="field"><span>有效时间</span><select value={hours} onChange={(event) => setHours(Number(event.target.value))}><option value={1}>1 小时</option><option value={24}>1 天</option><option value={168}>7 天</option><option value={720}>30 天</option></select></label></> : <label className="field"><span>地址类型</span><select value={kind} onChange={(event) => setKind(event.target.value)}><option value="mailbox">邮箱</option><option value="alias">别名</option><option value="temporary">临时地址</option></select></label>}{!batch && kind === "temporary" && <label className="field"><span>有效时间</span><input type="number" min={1} max={8760} value={hours} onChange={(event) => setHours(Number(event.target.value))} /></label>}{error && <div className="error">{error}</div>}<div className="form-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!batch && !localPart}><Plus size={16} />创建</button></div></form></Modal>;
}

function MemberForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [emailAddress, setEmailAddress] = useState("");
  const [role, setRole] = useState("operator");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/users", { method: "POST", body: JSON.stringify({ email: emailAddress, role }) });
      onCreated();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "添加失败");
    } finally {
      setBusy(false);
    }
  }
  return <Modal title="添加成员" onClose={onClose}><form className="form" onSubmit={submit}><label className="field"><span>成员邮箱</span><input type="email" value={emailAddress} onChange={(event) => setEmailAddress(event.target.value)} placeholder="name@example.com" /></label><label className="field"><span>平台角色</span><select value={role} onChange={(event) => setRole(event.target.value)}><option value="admin">管理员</option><option value="operator">操作员</option><option value="viewer">只读</option></select></label><div className="notice">添加后会发送登录链接。</div>{error && <div className="error">{error}</div>}<div className="form-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={busy || !emailAddress}><UserPlus size={16} />{busy ? "添加中" : "添加"}</button></div></form></Modal>;
}

function RuleForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: "", matchField: "recipient", matchType: "contains", pattern: "", destinations: "", priority: 100 });
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await api("/api/rules", { method: "POST", body: JSON.stringify({ ...form, destinations: form.destinations.split(",").map((item) => item.trim()).filter(Boolean), stopProcessing: false }) });
      onCreated();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "创建失败"); }
  }
  return <Modal title="添加转发规则" onClose={onClose}><form className="form" onSubmit={submit}><label className="field"><span>规则名称</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><div className="field-row"><label className="field"><span>匹配字段</span><select value={form.matchField} onChange={(event) => setForm({ ...form, matchField: event.target.value })}><option value="recipient">收件地址</option><option value="sender">发件地址</option><option value="subject">主题</option><option value="body">正文</option></select></label><label className="field"><span>匹配方式</span><select value={form.matchType} onChange={(event) => setForm({ ...form, matchType: event.target.value })}><option value="equals">完全相同</option><option value="contains">包含</option><option value="ends_with">结尾匹配</option><option value="regex">正则表达式</option><option value="any">全部</option></select></label></div><label className="field"><span>匹配内容</span><input value={form.pattern} onChange={(event) => setForm({ ...form, pattern: event.target.value })} disabled={form.matchType === "any"} /></label><label className="field"><span>转发到</span><input value={form.destinations} onChange={(event) => setForm({ ...form, destinations: event.target.value })} placeholder="多个地址用逗号分隔" /></label><label className="field"><span>优先级</span><input type="number" value={form.priority} onChange={(event) => setForm({ ...form, priority: Number(event.target.value) })} /></label>{error && <div className="error">{error}</div>}<div className="form-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!form.name || !form.destinations}>添加规则</button></div></form></Modal>;
}

function PolicyForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ listType: "block", matchType: "address", value: "", note: "" });
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await api("/api/policies", { method: "POST", body: JSON.stringify(form) });
      onCreated();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "添加失败"); }
  }
  return <Modal title="添加名单" onClose={onClose}><form className="form" onSubmit={submit}><div className="field-row"><label className="field"><span>名单类型</span><select value={form.listType} onChange={(event) => setForm({ ...form, listType: event.target.value })}><option value="block">黑名单</option><option value="allow">白名单</option></select></label><label className="field"><span>匹配方式</span><select value={form.matchType} onChange={(event) => setForm({ ...form, matchType: event.target.value })}><option value="address">完整地址</option><option value="domain">域名</option><option value="contains">包含</option><option value="regex">正则表达式</option></select></label></div><label className="field"><span>匹配内容</span><input value={form.value} onChange={(event) => setForm({ ...form, value: event.target.value })} /></label><label className="field"><span>备注</span><input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} /></label>{error && <div className="error">{error}</div>}<div className="form-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!form.value}>添加</button></div></form></Modal>;
}

function TotpModal({ value, onClose, onDone }: { value: { secret: string; qr: string }; onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try { await api("/api/auth/totp/finish", { method: "POST", body: JSON.stringify({ code }) }); onDone(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "验证失败"); }
  }
  return <Modal title="配置动态验证码" onClose={onClose}><form className="form totp-form" onSubmit={submit}><img src={value.qr} alt="动态验证码二维码" /><p>使用验证器应用扫描二维码，然后输入六位验证码。</p><div className="secret"><code>{value.secret}</code><button type="button" onClick={() => copy(value.secret)}><Copy size={15} /></button></div><label className="field"><span>验证码</span><input inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} /></label>{error && <div className="error">{error}</div>}<div className="form-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={code.length !== 6}>启用</button></div></form></Modal>;
}

createRoot(document.getElementById("root")!).render(<React.StrictMode><App /></React.StrictMode>);
